import React, { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { useLiff } from "../../../liff/index.js";
import { fetchReport, type ReportData } from "../../../liff/api.js";
import {
  MobileShell, Card, CardHeader, MetricGrid, ProgressBar, MobileSpinner, PrimaryButton,
} from "../../../liff/components/Mobile.js";

export function ReportPlaceholderPage(): React.ReactElement {
  const { reportId } = useParams<{ reportId: string }>();
  const { session } = useLiff();
  const [data, setData] = useState<ReportData | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!session || !reportId) return;
    fetchReport(reportId, session.session_token)
      .then(setData)
      .catch((e: unknown) => setError((e as Error).message));
  }, [session, reportId]);

  if (error) return (
    <MobileShell title="無法載入報表"><Card><p className="text-sm text-danger">{error}</p></Card></MobileShell>
  );
  if (!data) return <MobileSpinner />;

  const kindLabel = { daily: "每日報表", weekly: "週報", monthly: "月報", custom: "自訂報表" }[data.kind];
  const statusLabel = data.status === "ready" ? "✓ 可下載" : data.status === "queued" ? "處理中" : "失敗";
  const pct = data.summary.completion_rate ?? 0;

  return (
    <MobileShell
      title={`${kindLabel} · ${data.date_local}`}
      subtitle={data.site_name ?? `Tenant ${data.tenant_id}`}
      footer={
        <PrimaryButton href={data.download_url ?? undefined} disabled={!data.download_url || data.status !== "ready"}>
          {data.status === "ready" ? `下載報表（${data.size_kb ?? "?"} KB）` : statusLabel}
        </PrimaryButton>
      }
    >
      <Card>
        <CardHeader title="完成率摘要" hint={`產生於 ${data.generated_at_local}`} />
        <p className="text-3xl font-semibold mb-3">{Math.round(pct * 100)}<span className="text-base text-text-secondary">%</span></p>
        <ProgressBar value={pct} />
      </Card>

      <MetricGrid items={[
        { label: "完成 / 應巡", value: `${data.summary.patrols_completed ?? "—"} / ${data.summary.patrols_expected ?? "—"}`, tone: "default" },
        { label: "漏巡", value: String(data.summary.overdue_count ?? "—"), tone: (data.summary.overdue_count ?? 0) > 0 ? "danger" : "success" },
        { label: "事件", value: String(data.summary.incidents_count ?? "—"), tone: (data.summary.incidents_count ?? 0) > 0 ? "warning" : "success" },
        { label: "狀態", value: statusLabel, tone: data.status === "ready" ? "success" : data.status === "failed" ? "danger" : "warning" },
      ]} />

      {data.preview_sections.map((sec, i) => (
        <Card key={i}>
          <CardHeader title={sec.title} />
          <ul className="space-y-1.5 text-sm">
            {sec.lines.map((line, j) => (
              <li key={j} className="text-text-secondary leading-relaxed">{line}</li>
            ))}
          </ul>
        </Card>
      ))}
    </MobileShell>
  );
}
